import { COUNTRIES } from '@digital-twins/shared-types'
import catalog from './socialLanguageCatalog.json'

/**
 * The market as a social form holds it: the name shown and written as the
 * Library tag, and the ISO 3166-1 alpha-2 code of the country list entry the
 * user picked (the provider `region`); null when the market is not a list
 * entry (no single country, or a market stored before codes existed).
 */
export interface SocialMarketValue {
  market: string
  marketCode: string | null
}

/** The option of a stored market that is not a country list entry. */
export const SOCIAL_UNLISTED_MARKET = '__unlisted__'

/** The market picker's value: the picked entry's code, the unlisted option, or none. */
export function socialMarketSelectValue(value: SocialMarketValue): string {
  if (value.marketCode) {
    return value.marketCode
  }
  return value.market.trim() ? SOCIAL_UNLISTED_MARKET : ''
}

/**
 * The market an option of the picker stands for. A country option's value is
 * its entry's code, so the name and code both come from that entry; the
 * unlisted option keeps the current market as it is.
 */
export function socialMarketFromSelect(
  option: string,
  current: SocialMarketValue,
): SocialMarketValue {
  const entry = COUNTRIES.find((country) => country.code === option)
  return entry ? { market: entry.name, marketCode: entry.code } : current
}

const LANGUAGE_CODES: ReadonlySet<string> = new Set(
  catalog.languages.map((entry) => entry.code),
)

/**
 * A market's default language, from its country code: CLDR's likely language
 * for the country (`Intl.Locale` maximize, a typed lookup on the code), as
 * the language picker's regional locale when it lists one (Mexico: es-MX,
 * United States: en-US), else the bare language. Null without a code, or
 * when the picker lists neither.
 */
export function socialMarketDefaultLanguage(marketCode: string | null): string | null {
  if (!marketCode) {
    return null
  }
  let language: string
  try {
    language = new Intl.Locale('und', { region: marketCode }).maximize().language
  } catch {
    // Not a region code Intl knows: no default, the languages stay as they are.
    return null
  }
  const regional = `${language}-${marketCode}`
  if (LANGUAGE_CODES.has(regional)) {
    return regional
  }
  return LANGUAGE_CODES.has(language) ? language : null
}
